import React, { useContext, useEffect, useState } from 'react';
import {
	Autocomplete,
	Box,
	TextField,
	Popper,
	MenuItem,
	Typography,
	Tooltip,
} from '@mui/material';
import { ConfigContext } from 'src/base-ui/lib';
import http from 'src/base-ui/utils/http';
import auth from 'src/base-ui/utils/auth';
import { parseJwt } from 'src/base-ui/utils/jwt';

function UpwardPopper(props: any) {
	return (
		<Popper
			{...props}
			placement="top-start"
			modifiers={[
				{
					name: 'flip',
					enabled: false,
				},
				{
					name: 'offset',
					options: {
						offset: [0, 13],
					},
				},
			]}
			style={{ width: props.style?.width }}
		/>
	);
}

export function OrganizationSelector() {
	const { config } = useContext<any>(ConfigContext);
	const [tenants, setTenants] = useState<{ label: string; value: string }[]>(
		[]
	);
	const [currentTenantId, setCurrentTenantId] = useState<string>('');
	const [currentTenantName, setCurrentTenantName] = useState<string>('');

	useEffect(() => {
		async function fetchTenants() {
			const jwt = auth.getBearerToken();
			const tenantId = parseJwt(jwt).tenantId;
			setCurrentTenantId(tenantId);
			const result = await http.get(
				{ pageSize: 1000, pageOffset: 0, deleted: false },
				`${config.uri.base}/tenant`
			);
			const options = result.rows.map((x: any) => ({
				label: x.name,
				value: x._id,
			}));
			setTenants(options);
			const currentWorkspace = await http.get(
				{},
				`${config.uri.base}/tenant/${tenantId}`
			);
			setCurrentTenantName(currentWorkspace.name);
		}
		fetchTenants();
	}, [config]);

	const handleSwitch = async (tenantId: string) => {
		const platformJWT = await http.get(
			{},
			`${config.uri.base}/tenant/switch?tenantId=${tenantId}`,
			{ withCredentials: true }
		);
		auth.setBearerToken(platformJWT);
		window.location.href = '/quotations';
	};

	const selectedTenant =
		tenants.find((t) => t.value === currentTenantId) || null;

	return (
		<Box sx={{ width: '100%' }}>
			<Autocomplete
				options={[
					{ label: 'Switch Organization', value: '__header__' },
					...tenants,
				]}
				value={selectedTenant}
				getOptionLabel={(option) => option.label}
				slots={{
					popper: UpwardPopper,
				}}
				slotProps={{
					listbox: {
						style: {
							maxHeight: 200,
							overflowY: 'auto',
						},
					},
				}}
				renderOption={(props, option) => {
					// If it's the header, render differently
					if (option.value === '__header__') {
						return (
							<MenuItem disabled>
								<Typography variant="button">Switch Organization</Typography>
							</MenuItem>
						);
					}
					return (
						<Tooltip title={option.label} placement="right" arrow>
							<li
								{...props}
								style={{
									paddingTop: 10,
									paddingBottom: 10,
									marginBottom: 4,
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								}}
							>
								{option.label}
							</li>
						</Tooltip>
					);
				}}
				onChange={(_, newValue) => {
					if (!newValue || newValue.value === '__header__') return;
					if (newValue.value !== currentTenantId) {
						handleSwitch(newValue.value);
					}
				}}
				renderInput={(params) => (
					<Tooltip title={selectedTenant?.label || ''} placement="top" arrow>
						<TextField
							{...params}
							label="Select Organization"
							size="small"
							color="primary"
							sx={{
								'& .MuiInputBase-input': {
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
								},
							}}
						/>
					</Tooltip>
				)}
			/>
		</Box>
	);
}
